import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Subject } from 'rxjs';
import { NotificationService } from '@progress/kendo-angular-notification';
import {
  WhatsAppTemplateEdit,
  TemplateVariable,
  TEMPLATE_VARIABLES,
  ButtonActionType,
  TemplateButton,
  ButtonUrlType,
  VariableCategory,
  BUTTON_LINK_OPTIONS,
  ButtonLinkOption
} from './whatsapp-cloud-template-editor.interface';
import { CloudWhatsappService } from '../../services/cloud-whatsapp.service';

@Component({
  selector: 'app-whatsapp-cloud-template-editor',
  templateUrl: './whatsapp-cloud-template-editor.component.html',
  styleUrls: ['./whatsapp-cloud-template-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WhatsAppCloudTemplateEditorComponent implements OnInit, OnDestroy {

  // Dados do template recebidos via Input
  @Input() templateData: WhatsAppTemplateEdit;

  // Estados de loading
  salvando = false;
  carregandoTemplate = false;

  // Estados de UI
  showAddButtonDropdown = false;

  // Modal de erro
  showErrorModal = false;
  errorModalMessage = '';

  // Estado de validacao - mostra erros apos primeira tentativa de salvar
  tentouSalvar = false;

  // Estado de foco visual (Blueprint sidebar)
  activeField: 'header' | 'body' | 'footer' = 'body';

  // Controle de Abas Mobile (Editor vs Preview)
  mobileTab: 'editor' | 'preview' = 'editor';

  // Dados do template
  template: WhatsAppTemplateEdit;
  originalTemplate: WhatsAppTemplateEdit;

  // Variaveis disponiveis para insercao
  availableVariables = TEMPLATE_VARIABLES;

  // Links disponiveis para botoes de URL
  buttonLinkOptions: ButtonLinkOption[] = BUTTON_LINK_OPTIONS;

  // Categorias para agrupamento no dropdown
  private readonly categoryLabels: { key: VariableCategory; label: string }[] = [
    { key: 'cliente', label: 'Cliente' },
    { key: 'empresa', label: 'Empresa' },
    { key: 'pedido', label: 'Pedido' },
    { key: 'fidelidade', label: 'Fidelidade' },
    { key: 'links', label: 'Links' }
  ];

  // Getter para variaveis agrupadas por categoria
  get groupedVariables(): { category: VariableCategory; label: string; variables: TemplateVariable[] }[] {
    return this.categoryLabels.map(cat => ({
      category: cat.key,
      label: cat.label,
      variables: this.availableVariables.filter(v => v.category === cat.key)
    })).filter(g => g.variables.length > 0);
  }

  // Variaveis de simulacao (copia editavel)
  simulationVariables: TemplateVariable[] = [];

  // Mapa para persistir valores da simulacao
  private simulationValues: { [key: string]: string } = {};

  // Dropdown de variaveis aberto
  showVariableDropdown = false;

  // Constantes
  readonly MAX_HEADER_LENGTH = 60;
  readonly MAX_BODY_LENGTH = 1024;
  readonly MAX_FOOTER_LENGTH = 60;
  readonly MAX_BUTTON_TEXT_LENGTH = 25;
  readonly MAX_BUTTONS_TOTAL = 3;
  readonly MAX_BUTTONS_LINK = 2;

  private destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private cloudWhatsappService: CloudWhatsappService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.initializeTemplate();
    this.initializeSimulationVariables();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ========== Metodos Publicos ==========

  switchMobileTab(tab: 'editor' | 'preview'): void {
    this.mobileTab = tab;
    this.cdr.markForCheck();
  }

  /**
   * Inicializa o template com dados recebidos via Input, navigation state ou carrega da API
   */
  private async initializeTemplate(): Promise<void> {
    if (this.templateData) {
      this.setTemplateData(this.templateData);
      return;
    }

    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state as { template: WhatsAppTemplateEdit } | undefined;
    const historyState = history.state as { template: WhatsAppTemplateEdit } | undefined;
    const templateFromState = state?.template || historyState?.template;

    if (templateFromState) {
      this.setTemplateData(templateFromState);
      return;
    }

    // Se não tem state, tenta carregar da API usando o ID da rota
    const idParam = this.route.snapshot.params['id'];
    if (idParam) {
      const templateId = parseInt(idParam, 10);
      if (!isNaN(templateId)) {
        await this.carregueTemplateDaApi(templateId);
        return;
      }
    }

    // Fallback: template vazio (não deveria acontecer em uso normal)
    this.template = {
      nomeTemplate: '',
      tipoNotificacao: '',
      status: 'DRAFT',
      categoria: 'UTILITY',
      headerText: '',
      bodyText: '',
      footerText: '',
      buttonCategory: 'NONE',
      buttons: []
    };
    this.originalTemplate = JSON.parse(JSON.stringify(this.template));
  }

  /**
   * Carrega o template da API pelo ID (usado quando state é perdido após F5)
   */
  private async carregueTemplateDaApi(id: number): Promise<void> {
    try {
      this.carregandoTemplate = true;
      this.cdr.markForCheck();

      console.log('[WhatsApp Editor] Carregando template da API, ID:', id);

      const templateData = await this.cloudWhatsappService.obtenhaTemplatePorId(id);

      if (templateData) {
        this.setTemplateData(templateData as WhatsAppTemplateEdit);
        this.initializeSimulationVariables();
        console.log('[WhatsApp Editor] Template carregado da API com sucesso');
      } else {
        console.error('[WhatsApp Editor] Template não encontrado');
        this.notificationService.show({
          content: 'Template não encontrado.',
          cssClass: 'error',
          animation: { type: 'slide', duration: 400 },
          position: { horizontal: 'right', vertical: 'top' },
          type: { style: 'error', icon: true }
        });
        this.voltar();
      }
    } catch (erro: any) {
      console.error('[WhatsApp Editor] Erro ao carregar template da API:', erro);
      this.notificationService.show({
        content: erro?.message || 'Erro ao carregar template.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
      this.voltar();
    } finally {
      this.carregandoTemplate = false;
      this.cdr.markForCheck();
    }
  }

  private setTemplateData(data: WhatsAppTemplateEdit): void {
    this.template = {
      ...data,
      buttons: [...(data.buttons || [])]
    };
    this.originalTemplate = JSON.parse(JSON.stringify(data));
  }

  carregueTemplate(data: WhatsAppTemplateEdit): void {
    this.setTemplateData(data);
    this.initializeSimulationVariables();
    this.cdr.markForCheck();
  }

  updateSimulationList(): void {
    const text = (this.template.headerText || '') + (this.template.bodyText || '');
    const usedPlaceholders = new Set<string>();
    const regex = /\{\{[^}]+\}\}/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      usedPlaceholders.add(match[0]);
    }

    this.simulationVariables = this.availableVariables
      .filter(v => usedPlaceholders.has(v.placeholder))
      .map(v => ({
        ...v,
        simulationValue: this.simulationValues[v.placeholder] || v.simulationValue
      }));

    this.cdr.markForCheck();
  }

  voltar(): void {
    this.location.back();
  }

  descarte(): void {
    this.template = JSON.parse(JSON.stringify(this.originalTemplate));
    this.voltar();
  }

  fecheModalErro(): void {
    this.showErrorModal = false;
    this.errorModalMessage = '';
    this.cdr.markForCheck();
  }

  async salve(): Promise<void> {
    this.tentouSalvar = true;
    this.cdr.markForCheck();

    if (!this.valideTemplate()) {
      return;
    }

    // Verificar se o template tem ID (necessário para atualização)
    if (!this.template.id) {
      this.notificationService.show({
        content: 'Template sem ID. Não é possível atualizar.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
      return;
    }

    try {
      this.salvando = true;
      this.cdr.markForCheck();

      console.log('[WhatsApp Editor] Enviando atualização para a Meta...');

      // Chamar o serviço para atualizar na Meta e no banco
      const resultado = await this.cloudWhatsappService.atualizeTemplate(
        this.template.id,
        {
          headerText: this.template.headerText,
          bodyText: this.template.bodyText,
          footerText: this.template.footerText,
          buttons: this.template.buttons.map(b => ({
            actionType: b.actionType,
            text: b.text,
            value: b.value,
            urlType: b.urlType,
            linkKey: b.linkKey
          }))
        }
      );

      console.log('[WhatsApp Editor] Resultado da atualização:', resultado);

      if (!resultado.sucesso) {
        // Exibe mensagem de erro da Meta
        this.notificationService.show({
          content: resultado.mensagem || 'Erro ao atualizar template na Meta.',
          cssClass: 'error',
          animation: { type: 'slide', duration: 400 },
          position: { horizontal: 'right', vertical: 'top' },
          type: { style: 'error', icon: true }
        });
        return; // Não volta para a tela anterior se deu erro
      }

      // Sucesso
      this.notificationService.show({
        content: 'Template atualizado com sucesso!',
        cssClass: 'success',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'success', icon: true }
      });

      this.voltar();
    } catch (erro: any) {
      console.error('[WhatsAppCloudTemplateEditor] Erro ao salvar:', erro);
      // O erro pode ser uma string (do ServerService) ou um objeto com message
      const mensagemErro = typeof erro === 'string' ? erro : (erro?.message || 'Erro ao salvar template.');
      // Exibe modal de erro no centro da tela
      this.errorModalMessage = mensagemErro;
      this.showErrorModal = true;
    } finally {
      this.salvando = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Transforma o template com variáveis nomeadas (ex: {{nome}})
   * em formato compatível com a Meta (ex: {{1}}) e gera os exemplos.
   */
  private prepareApiPayload(): any {
    const processComponent = (text: string | undefined) => {
      if (!text) return { text: undefined, examples: [] };

      const variables: string[] = [];
      const processedText = text.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
        // Verifica se a variável já foi mapeada neste componente (para manter coerência se repetida)
        // OBS: Para a Meta, se a mesma variável aparecer 2x no corpo, ela é {{1}} e {{2}} ou {{1}} e {{1}}?
        // A documentação sugere parâmetros posicionais sequenciais.
        // Se usarmos {{1}} e {{1}}, o mesmo valor preencherá ambos.
        // Nossa lógica atual: cada ocorrência recebe um novo índice sequencial {{1}}, {{2}}...
        // Isso dá mais flexibilidade (ex: dois nomes diferentes).

        variables.push(variable); // Guarda o nome original
        return `{{${variables.length}}}`; // Substitui por {{1}}, {{2}}...
      });

      // Mapeia os nomes das variáveis para seus valores de simulação
      const examples = variables.map(varName => {
        const simVar = this.simulationVariables.find(v => v.placeholder === `{{${varName}}}`);
        return simVar ? simVar.simulationValue : 'Exemplo';
      });

      return { text: processedText, examples };
    };

    const header = processComponent(this.template.headerText);
    const body = processComponent(this.template.bodyText);

    return {
      name: this.template.nomeTemplate,
      category: this.template.categoria,
      components: [
        ...(header.text ? [{
          type: 'HEADER',
          format: 'TEXT',
          text: header.text,
          example: { header_text: [header.examples] }
        }] : []),
        {
          type: 'BODY',
          text: body.text,
          example: { body_text: [body.examples] }
        },
        ...(this.template.footerText ? [{
          type: 'FOOTER',
          text: this.template.footerText
        }] : []),
        ...(this.template.buttons.length > 0 ? [{
          type: 'BUTTONS',
          buttons: this.template.buttons.map(b => {
             if (b.actionType === 'QUICK_REPLY') {
               return { type: 'QUICK_REPLY', text: b.text };
             }
             if (b.actionType === 'VISITAR_SITE') {
               return { type: 'URL', text: b.text, url: b.value };
             }
             return b;
          })
        }] : [])
      ]
    };
  }


  apliqueBold(): void {
    this.aplicarFormatacao('*');
  }

  apliqueItalico(): void {
    this.aplicarFormatacao('_');
  }

  apliqueStrikethrough(): void {
    this.aplicarFormatacao('~');
  }

  toggleVariableDropdown(): void {
    this.showVariableDropdown = !this.showVariableDropdown;
    this.cdr.markForCheck();
  }

  insiraVariavel(variable: TemplateVariable): void {
    const isHeader = this.activeField === 'header';
    const elementId = isHeader ? 'headerInput' : 'bodyTextarea';
    const inputEl = document.getElementById(elementId) as HTMLInputElement | HTMLTextAreaElement;

    if (!inputEl) {
      console.warn(`[WhatsApp Editor] Elemento ${elementId} não encontrado.`);
      return;
    }

    const start = inputEl.selectionStart || 0;
    const end = inputEl.selectionEnd || 0;
    const currentText = isHeader ? (this.template.headerText || '') : (this.template.bodyText || '');

    const newText =
      currentText.substring(0, start) +
      variable.placeholder +
      currentText.substring(end);

    if (isHeader) {
      this.template.headerText = newText;
    } else {
      this.template.bodyText = newText;
    }

    this.showVariableDropdown = false;

    setTimeout(() => {
      inputEl.focus();
      const newPos = start + variable.placeholder.length;
      inputEl.setSelectionRange(newPos, newPos);
    });

    this.updateSimulationList();
    this.cdr.markForCheck();
  }

  // ========== Gerenciamento de Botoes ==========

  toggleAddButtonDropdown(): void {
    this.showAddButtonDropdown = !this.showAddButtonDropdown;
    this.cdr.markForCheck();
  }

  /**
   * Helper: Conta quantos links ja existem
   */
  get countLinks(): number {
    return this.template.buttons.filter(b => b.actionType === 'VISITAR_SITE').length;
  }

  /**
   * Helper: Pode adicionar mais botoes no geral?
   */
  get canAddMoreButtons(): boolean {
    return this.template.buttons.length < this.MAX_BUTTONS_TOTAL;
  }

  /**
   * Helper: Pode adicionar mais links?
   */
  get canAddMoreLinks(): boolean {
    return this.countLinks < this.MAX_BUTTONS_LINK;
  }

  /**
   * Adiciona novo botao baseado no tipo solicitado
   */
  adicioneButton(type: ButtonActionType): void {
    // 1. Valida total
    if (this.template.buttons.length >= this.MAX_BUTTONS_TOTAL) {
      this.notificationService.show({
        content: `Máximo de ${this.MAX_BUTTONS_TOTAL} botões no total.`,
        cssClass: 'warning',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'warning', icon: true }
      });
      return;
    }

    // 2. Valida links
    if (type === 'VISITAR_SITE' && this.countLinks >= this.MAX_BUTTONS_LINK) {
      this.notificationService.show({
        content: `Máximo de ${this.MAX_BUTTONS_LINK} botões de link permitidos.`,
        cssClass: 'warning',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'warning', icon: true }
      });
      return;
    }

    const newButton: TemplateButton = {
      id: this.gerarIdUnico(),
      actionType: type,
      text: '',
      value: ''
    };

    if (type === 'VISITAR_SITE') {
      newButton.urlType = 'STATIC';
    }

    this.template.buttons.push(newButton);
    this.showAddButtonDropdown = false;
    this.cdr.markForCheck();
  }

  removaButton(index: number): void {
    this.template.buttons.splice(index, 1);
    this.cdr.markForCheck();
  }

  // ========== Metodos de Preview ==========

  obtenhaPreviewBody(): string {
    let preview = this.template.bodyText || '';

    for (const variable of this.simulationVariables) {
      const colorClass = variable.color ? `color-${variable.color}` : 'color-green';
      preview = preview.replace(
        new RegExp(this.escapeRegex(variable.placeholder), 'g'),
        `<span class="variable-highlight tooltip-container ${colorClass}">${variable.simulationValue}<span class="tooltip-content"><span class="tooltip-label">${variable.label.toUpperCase()}</span><span class="tooltip-key">${variable.placeholder}</span></span></span>`
      );
    }

    preview = this.converterFormatacao(preview);
    return preview;
  }

  obtenhaPreviewHeader(): string {
    let preview = this.template.headerText || '';
    for (const variable of this.simulationVariables) {
      preview = preview.replace(
        new RegExp(this.escapeRegex(variable.placeholder), 'g'),
        variable.simulationValue
      );
    }
    return preview;
  }

  atualizeSimulacao(index: number, valor: string): void {
    const variable = this.simulationVariables[index];
    variable.simulationValue = valor;
    this.simulationValues[variable.placeholder] = valor;
    this.cdr.markForCheck();
  }

  obtenhaContagemVariaveis(): number {
    const bodyMatches = (this.template.bodyText || '').match(/\{\{[^}]+\}\}/g) || [];
    const headerMatches = (this.template.headerText || '').match(/\{\{[^}]+\}\}/g) || [];
    return bodyMatches.length + headerMatches.length;
  }

  obtenhaCorStatus(status: string): string {
    if (!status) return 'status-draft';
    const statusLower = status.toLowerCase();
    if (statusLower === 'approved') return 'status-approved';
    if (statusLower === 'pending' || statusLower === 'pending_quality_update') return 'status-pending';
    if (statusLower === 'rejected') return 'status-rejected';
    if (statusLower === 'paused') return 'status-paused';
    return 'status-draft';
  }

  obtenhaIconeStatus(status: string): string {
    if (!status) return 'fa-pen-ruler'; // Icon for DRAFT
    const statusLower = status.toLowerCase();
    if (statusLower === 'approved') return 'fa-check-circle';
    if (statusLower === 'pending' || statusLower === 'pending_quality_update') return 'fa-hourglass-half';
    if (statusLower === 'rejected') return 'fa-times-circle';
    if (statusLower === 'paused') return 'fa-pause-circle';
    return 'fa-pen-ruler';
  }

  getTranslatedStatus(status: string): string {
    if (!status) return 'Rascunho';
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'approved': return 'Aprovado';
      case 'pending':
      case 'pending_quality_update': return 'Aguardando Aprovação';
      case 'rejected': return 'Rejeitado';
      case 'paused': return 'Pausado';
      case 'draft': return 'Rascunho';
      default: return status; // Fallback for unknown statuses
    }
  }

  // ========== Metodos Privados ==========

  private initializeSimulationVariables(): void {
    this.availableVariables.forEach(v => {
      this.simulationValues[v.placeholder] = v.simulationValue;
    });
    this.updateSimulationList();
  }

  private aplicarFormatacao(marker: string): void {
    const textarea = document.getElementById('bodyTextarea') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = this.template.bodyText || '';
    const selectedText = text.substring(start, end);

    if (selectedText) {
      this.template.bodyText =
        text.substring(0, start) +
        marker + selectedText + marker +
        text.substring(end);
    }

    this.cdr.markForCheck();
  }

  private converterFormatacao(text: string): string {
    text = text.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
    text = text.replace(/_([^_]+)_/g, '<em>$1</em>');
    text = text.replace(/~([^~]+)~/g, '<s>$1</s>');
    const urlRegex = /((https?:\/\/[^\s<]+)|(www\.[^\s<]+))/g;
    text = text.replace(urlRegex, '<span class="message-link">$1</span>');
    text = text.replace(/\n/g, '<br>');
    return text;
  }

  private escapeRegex(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private gerarIdUnico(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private valideTemplate(): boolean {
    // Valida conteudo obrigatorio
    if (!this.template.bodyText?.trim()) {
      return false;
    }

    // Valida botoes
    for (const btn of this.template.buttons) {
      if (!btn.text?.trim()) {
        return false;
      }

      if (btn.actionType === 'VISITAR_SITE' && !btn.linkKey) {
        return false;
      }
    }

    return true;
  }
}
